import { useCallback, useLayoutEffect, type ChangeEvent } from 'react';
import { motion } from 'motion/react';
import { Send, Square, X } from 'lucide-react';
import MemoryPanel from './MemoryPanel';
import ModePicker from './ModePicker';
import SlashCommandPicker from './SlashCommandPicker';
import { resizeTextarea } from '../ui/AutoGrowTextarea';
import {
  inputArea,
  inputWrapper,
  inputTextarea,
  sendActiveClass,
  sendInactiveClass,
  stopButtonClass,
} from './chatViewTheme';

const MAX_INPUT_HEIGHT_PX = 128; // max-h-32

export default function AIChatInput({
  memoryPanelOpen,
  onCloseMemory,
  input,
  inputRef,
  onInputChange,
  onKeyDown,
  onSend,
  onStop,
  loading,
  streamingMessageId,
  onCooldown,
  chatMode,
  onModeChange,
  reduceMotion,
  queuedQuestion,
  // slash commands
  slashPickerOpen,
  slashQuery,
  slashSelectedIndex,
  onSlashSelect,
  formatModifier,
  onClearFormatModifier,
}: any) {
  const canSend = !onCooldown && input.trim();

  useLayoutEffect(() => {
    resizeTextarea(inputRef.current, MAX_INPUT_HEIGHT_PX);
  });

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      onInputChange(e);
      resizeTextarea(e.target, MAX_INPUT_HEIGHT_PX);
    },
    [onInputChange],
  );

  return (
    <div className={`relative ${inputArea}`}>
      <MemoryPanel open={memoryPanelOpen} onClose={onCloseMemory} />

      {/* Slash command picker — floats above input */}
      <SlashCommandPicker
        open={slashPickerOpen}
        query={slashQuery}
        selectedIndex={slashSelectedIndex}
        onSelect={onSlashSelect}
      />

      <div className="flex flex-col gap-2">
        {input.length > 18000 && (
          <p className="text-xs text-amber-400">{20000 - input.length} characters remaining</p>
        )}

        <div className={inputWrapper}>
          <textarea
            ref={inputRef}
            value={input}
            onChange={handleInputChange}
            onKeyDown={onKeyDown}
            placeholder={
              formatModifier
                ? `Ask a question — will be presented as ${formatModifier.label.toLowerCase()}…`
                : 'Ask Vanti a question… (type / for commands)'
            }
            rows={1}
            maxLength={20000}
            className={inputTextarea}
            disabled={onCooldown}
          />
          {loading || streamingMessageId ? (
            <motion.button
              type="button"
              onClick={onStop}
              whileHover={reduceMotion ? undefined : { scale: 1.08 }}
              whileTap={reduceMotion ? undefined : { scale: 0.92 }}
              className={stopButtonClass}
              aria-label="Stop generating"
              title="Stop generating"
            >
              <Square className="h-4 w-4 fill-current" aria-hidden />
            </motion.button>
          ) : (
            <button
              type="button"
              onClick={onSend}
              disabled={!canSend}
              className={canSend ? sendActiveClass : sendInactiveClass}
              aria-label="Send"
            >
              <Send className="h-4 w-4" aria-hidden />
            </button>
          )}
        </div>

        {/* Bottom toolbar */}
        <div className="flex items-center gap-2">
          <ModePicker value={chatMode} onChange={onModeChange} />

          {/* Active format modifier badge */}
          {formatModifier && (
            <span className="flex items-center gap-1 rounded-full border border-amber-700/40 bg-amber-900/20 px-2.5 py-1 text-[11px] text-amber-300">
              {formatModifier.label}
              <button
                type="button"
                onClick={onClearFormatModifier}
                className="ml-0.5 rounded-full p-0.5 hover:bg-amber-800/30"
                aria-label="Clear format modifier"
              >
                <X className="h-2.5 w-2.5" aria-hidden />
              </button>
            </span>
          )}

          {/* Queued question label */}
          {queuedQuestion && !formatModifier && (
            <span className="truncate text-xs text-slate-500 italic">
              Queued: &quot;{queuedQuestion.length > 40 ? queuedQuestion.slice(0, 40) + '…' : queuedQuestion}&quot;
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
